import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { addEventListener as _$addEventListener } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=vignette-overlay>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=top-bar><div class=top-bar-left><button class="wordmark wordmark-btn"aria-label="Open layer menu">ATLAS INTEL</button><div class=tagline>NO PLACE LEFT BEHIND</div></div><div class=top-bar-center></div><div class=top-bar-right><div class=mode-indicator></div><div class=rec-section><div class=rec-indicator><span id=live-clock></span></div><div class=telemetry id=telemetry></div></div><button class=mobile-toggle aria-label="Open settings menu">⚙`),
  _tmpl$3 = /*#__PURE__*/_$template(`<div class=panel-backdrop>`);
/**
 * WorldView - Shell Component (SolidJS)
 * Main UI container with top bar, panels, and overlays
 *
 * b454: mobile-aware — adds tap-toggle hamburger/gear buttons,
 * panel backdrop for outside-tap dismiss, default panels closed on mobile.
 */

import { createSignal, onMount, onCleanup, Show } from "solid-js";
import { ui, toggleLeftPanel, toggleRightPanel, setLeftPanelOpen, setRightPanelOpen, setCommandMode } from "../stores/ui";
import { shaders, setShader } from "../stores/shaders";
import { LeftPanel } from "./LeftPanelComponent";
import { RightPanel } from "./RightPanelComponent";
import { BottomBar } from "./BottomBarComponent";
import { PerformanceMonitor } from "./PerformanceMonitorComponent";
import { CommandBar } from "./CommandBar";
import { useCesium } from "../cesium/useCesium";
import { useCCTVFetcher } from "../layers/ground/useCCTVFetcher";
// dummy from "../cesium/useCesium";
import { CCTVPanel } from "./panels/CCTVPanel";
import { TouchNav } from "./TouchNavComponent";

// 2026-09-09: shader render modes (keys 2–5: CRT/NVG/FLIR/AH64) removed for
// product honesty. Key "1" (NORMAL / clear shader) is all that remains.
const MODE_SHORTCUTS = {
  "1": null
};
const MOBILE_BREAKPOINT = 900;
// 2026-09-04 — TouchNav (+/- zoom, Brockmann "hard to Zoom" 08-31) was gated on
// innerWidth alone. Inside the iOS WKWebView the page mounts at frame .zero
// (ContentView.swift:6404 `WKWebView(frame: .zero)` then immediate load), so the
// width read at mount is not the phone's width, and the buttons showed on one
// device test and not the next with the SAME served bundle. Gate on touch
// capability too: a coarse pointer or any touch point is a phone regardless of
// what innerWidth says at mount. OR-ed, so nothing that showed before is hidden.
const isMobile = () => typeof window !== "undefined" && (window.innerWidth <= MOBILE_BREAKPOINT || (navigator.maxTouchPoints ?? 0) > 0 || typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches);
function formatUTCTimestamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`;
}
function generateTelemetry() {
  const rand = (max, w) => String(Math.floor(Math.random() * max)).padStart(w, "0");
  return `GRB: ${rand(99999, 5)} PASS: DESC:${rand(999, 3)}`;
}
function isTypingInInput() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
}
export function Shell() {
  const [time, setTime] = createSignal(formatUTCTimestamp());
  const [telemetry, setTelemetry] = createSignal(generateTelemetry());
  const [showFPS, setShowFPS] = createSignal(false);
  const [mobile, setMobile] = createSignal(isMobile());

  // 2026-08-31 (Brockmann: "panel is supposed to open when clicked, then close
  // to choose layers"). stores/ui.ts defaults BOTH panels to open. On a desktop
  // they sit beside the map; on a phone they sit ON TOP of it, so the app opened
  // with the globe reduced to a ~200px strip. Start closed on mobile — the map is
  // the product, the panels are a drawer you pull open.
  if (isMobile()) {
    setLeftPanelOpen(false);
    setRightPanelOpen(false);
  }
  const {
    viewer
  } = useCesium();
  useCCTVFetcher(); // load cameras on viewport change regardless of drawer state

  function flyHome() {
    const v = viewer();
    if (v && !v.isDestroyed()) {
      v.camera.flyHome(2.0);
    }
  }
  let clockInterval = null;
  let telemetryInterval = null;
  function startIntervals() {
    stopIntervals();
    setTime(formatUTCTimestamp());
    setTelemetry(generateTelemetry());
    clockInterval = setInterval(() => setTime(formatUTCTimestamp()), 1000);
    telemetryInterval = setInterval(() => setTelemetry(generateTelemetry()), 3000);
  }
  function stopIntervals() {
    if (clockInterval) {
      clearInterval(clockInterval);
      clockInterval = null;
    }
    if (telemetryInterval) {
      clearInterval(telemetryInterval);
      telemetryInterval = null;
    }
  }
  function handleVisibilityChange() {
    document.hidden ? stopIntervals() : startIntervals();
  }
  function handleResize() {
    setMobile(isMobile());
  }
  function handleKeydown(e) {
    if (e.key !== "Escape" && isTypingInInput()) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const actions = {
      "Escape": () => {
        if (ui.commandMode) setCommandMode(false);else if (mobile() && (ui.leftPanelOpen || ui.rightPanelOpen)) {
          setLeftPanelOpen(false);
          setRightPanelOpen(false);
        }
      },
      ":": () => setCommandMode(true),
      "f": () => setShowFPS(!showFPS()),
      "F": () => setShowFPS(!showFPS()),
      "[": toggleLeftPanel,
      "]": toggleRightPanel
    };
    const action = actions[e.key];
    if (action) {
      e.preventDefault();
      action();
      return;
    }
    const mode = MODE_SHORTCUTS[e.key];
    if (mode !== undefined) {
      e.preventDefault();
      setShader(mode);
    }
  }

  /**
   * Mobile-only: when one panel opens, close the other so they never
   * stack/overlap. Desktop keeps both open by default.
   */
  function openLeftMobile() {
    if (mobile() && ui.rightPanelOpen) setRightPanelOpen(false);
    toggleLeftPanel();
  }

  // 2026-08-31 MERGE (Brockmann: "the menu should be in settings" + "3 - merge").
  // The ⚙ used to open the RIGHT panel (telemetry / pixelation / distortion /
  // instability / live readout) while the LAYER menu — the one that actually
  // matters operationally — lived behind the wordmark. Two settings surfaces,
  // and the ⚙ opened the less useful one.
  // Now ⚙ opens the LAYER menu, which is what an operator means by settings:
  // what is drawn on the globe. The wordmark keeps toggling the same panel, so
  // both entry points lead to one place. The old parameters panel is still
  // reachable via its own control; it is a display-tuning surface, not settings.
  function openRightMobile() {
    if (mobile() && ui.rightPanelOpen) setRightPanelOpen(false);
    toggleLeftPanel();
  }
  // (old openRightMobile body removed 2026-08-31 — superseded by the merge above)

  function closeAllPanels() {
    setLeftPanelOpen(false);
    setRightPanelOpen(false);
  }
  onMount(() => {
    // On mobile boot: close both panels so the globe is visible
    if (mobile()) {
      setLeftPanelOpen(false);
      setRightPanelOpen(false);
    }
    startIntervals();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("keydown", handleKeydown);
    window.addEventListener("resize", handleResize);
    console.log("[Shell] Mounted, mobile=", mobile());
  });
  onCleanup(() => {
    stopIntervals();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("keydown", handleKeydown);
    window.removeEventListener("resize", handleResize);
  });
  const modeDisplay = () => shaders.active?.toUpperCase() ?? "NORMAL";
  const showBackdrop = () => mobile() && (ui.leftPanelOpen || ui.rightPanelOpen);
  return [_tmpl$(), (() => {
    var _el$2 = _tmpl$2(),
      _el$3 = _el$2.firstChild,
      _el$4 = _el$3.firstChild,
      _el$5 = _el$3.nextSibling,
      _el$6 = _el$5.nextSibling,
      _el$7 = _el$6.firstChild,
      _el$8 = _el$7.nextSibling,
      _el$9 = _el$8.firstChild,
      _el$0 = _el$9.firstChild,
      _el$1 = _el$9.nextSibling,
      _el$10 = _el$8.nextSibling;
    _$addEventListener(_el$4, "click", toggleLeftPanel, true);
    _$insert(_el$6, _$createComponent(Show, {
      get when() {
        return showFPS();
      },
      get children() {
        return _$createComponent(PerformanceMonitor, {});
      }
    }), _el$7);
    _$insert(_el$7, modeDisplay);
    _$insert(_el$0, time);
    _$insert(_el$1, telemetry);
    _el$10.$$click = openRightMobile;
    return _el$2;
  })(), _$createComponent(Show, {
    get when() {
      return showBackdrop();
    },
    get children() {
      var _el$11 = _tmpl$3();
      _el$11.$$click = closeAllPanels;
      return _el$11;
    }
  }), _$createComponent(Show, {
    get when() {
      return ui.leftPanelOpen;
    },
    get children() {
      return _$createComponent(LeftPanel, {});
    }
  }), _$createComponent(Show, {
    get when() {
      return ui.rightPanelOpen;
    },
    get children() {
      return _$createComponent(RightPanel, {});
    }
  }), _$createComponent(BottomBar, {}), _$createComponent(CommandBar, {}), _$createComponent(CCTVPanel, {}), _$createComponent(TouchNav, {
    get visible() {
      return mobile();
    }
  })];
}
export default Shell;
_$delegateEvents(["click"]);